import { useTheme } from '@automata/mui-theme';
import { Typography } from '@automata/ui';
import { Box, Divider } from '@mui/material';
import { Stack } from '@mui/system';
import { startCase } from 'lodash';
import { Fragment } from 'react';
import { Position } from 'reactflow';

import { DataHandleWithLabel } from '../../../../components/GraphEditor/DataHandle';
import NodeBackground from '../../../../components/GraphEditor/NodeBackground';
import { selectDataDependencies } from '../../../../features/workflowJsonSelectors';
import { useAppSelector } from '../../../../hooks';
import {
  ConditionalTaskProperties,
  LabwareDataForTaskByHandlePairId,
} from '../../../../types';
import ConditionLabwareSection from '../TaskNode/ConditionLabwareSection';
import EmptyLabwareSection from '../TaskNode/EmptyLabwareSection';
import LabwareInTask from '../TaskNode/ExistingLabwareSection';
import {
  useConditionTaskHandleComponents,
  useLabwareHandleComponents,
} from '../TaskNode/utils';

type Props = {
  properties: ConditionalTaskProperties;
  selected: boolean;
  labwareDataByHandle: LabwareDataForTaskByHandlePairId;
  orderedHandlePairIds: string[];
  indicators: JSX.Element | null;
};

export const ConditionNode = ({
  selected,
  labwareDataByHandle,
  orderedHandlePairIds,
  properties: { id, nickname, taskType, propertiesType },
  indicators,
}: Props) => {
  const theme = useTheme();
  const sourceData = useAppSelector(selectDataDependencies)[id]?.source_data;

  const sourceDataName =
    sourceData &&
    'output_name' in sourceData &&
    typeof sourceData.output_name === 'string'
      ? startCase(sourceData.output_name)
      : '';

  const labwareLabelledHandleComponents = useLabwareHandleComponents({
    instrumentCapacity: 1,
    labwareDataByHandle,
    orderedHandlePairIds,
    taskId: id,
    taskType,
  });

  const labwareLabelledHandleComponent = labwareLabelledHandleComponents[0];
  const { inputs, outputs } = useConditionTaskHandleComponents({
    taskId: id,
  });

  return (
    <Stack direction="row" pr={2}>
      {/* Label */}
      <Box
        textAlign="right"
        position="absolute"
        left="0"
        top="50px"
        sx={{ transform: 'translate(-100%) translate(-0.5rem)' }}
        bgcolor="rgba(248, 248, 248, 0.8)"
        borderRadius={1}
        p={1}
      >
        <Typography variant="h6">{nickname ?? propertiesType}</Typography>
        {indicators}
      </Box>
      {/* Node box & contents */}
      <NodeBackground isSelected={selected} padding={0}>
        <Stack direction="column" justifyContent="center" spacing={1}>
          <Stack
            direction="row"
            justifyContent="center"
            spacing={1}
            p={'16px 16px 8px 16px'}
          >
            {labwareLabelledHandleComponent?.handleType === 'labware' && (
              <LabwareInTask
                hasDeadEnd={true}
                handlePairId={labwareLabelledHandleComponent.handlePairId}
                data={labwareLabelledHandleComponent.labwareData}
              />
            )}
            {labwareLabelledHandleComponent?.handleType === 'addLabware' && (
              <EmptyLabwareSection
                hasDeadEnd={true}
                handlePairId={labwareLabelledHandleComponent.handlePairId}
              />
            )}
            <Box sx={{ paddingY: 0, zIndex: 2 }}>
              <Divider orientation="vertical" />
            </Box>
            {inputs.map((component) => {
              return (
                <DataHandleWithLabel
                  key={component.handleId}
                  id={component.handleId}
                  name={sourceDataName || component.name}
                  position={Position.Top}
                  isConnected={component.isConnected}
                  handleStyle={{ top: '-24px' }}
                />
              );
            })}
          </Stack>
          <Stack
            direction="row"
            justifyContent="center"
            sx={{ backgroundColor: theme.palette.grey[800] }}
            spacing={1}
            p="8px 16px 16px 16px"
          >
            {outputs.map((component, index) => {
              return (
                <Fragment key={component.handleId}>
                  <ConditionLabwareSection
                    handlePairId={
                      labwareLabelledHandleComponent
                        ? `${labwareLabelledHandleComponent.handlePairId}.${index}`
                        : ''
                    }
                    label={component.name}
                    color={
                      labwareLabelledHandleComponent?.handleType ===
                        'labware' &&
                      labwareLabelledHandleComponent?.labwareData.colorIndex
                    }
                  />
                  {index !== outputs.length - 1 && (
                    <Box
                      sx={{ paddingY: 0, zIndex: 2, backgroundColor: 'white' }}
                    >
                      <Divider orientation="vertical" />
                    </Box>
                  )}
                </Fragment>
              );
            })}
          </Stack>
        </Stack>
      </NodeBackground>
    </Stack>
  );
};
